import React, { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  LocalUser,
  RemoteUser,
  useJoin,
  useIsConnected,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  usePublish,
  useRemoteAudioTracks,
  useRemoteUsers,
} from "agora-rtc-react";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone, faPhoneSlash } from "@fortawesome/free-solid-svg-icons";
import {
  generateLiveClassToken,
  get,
  getActiveClasses,
} from "@/actions/courses.actions";
import { UserContext } from "@/context/UserContext";
import CRC32 from "crc-32";

export default function Room({ resource }) {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { currentCourse, accessToken, activeClasses } = useSelector(
    (state: any) => state.courses
  );
  const userContext = useContext(UserContext);
  const [isInstructor, setIsInstructor] = useState(false);
  const [token, setToken] = useState("");
  const [uid, setUid] = useState(0);
  const [classes, setClasses] = useState([]);

  // set the connection state
  const [calling, setCalling] = useState(false);
  const isConnected = useIsConnected();

  //local user
  const [micOn, setMic] = useState(true);
  const [cameraOn, setCamera] = useState(true);
  const { localMicrophoneTrack } = useLocalMicrophoneTrack(micOn);
  const { localCameraTrack } = useLocalCameraTrack(cameraOn);
  usePublish([localMicrophoneTrack, localCameraTrack]);

  //remote users
  const remoteUsers = useRemoteUsers();
  const { audioTracks } = useRemoteAudioTracks(remoteUsers);

  if (!userContext) {
    throw new Error("UserContext must be used within a UserProvider");
  }

  const { user } = userContext;
  console.log(user);
  const APP_ID = process.env.REACT_APP_AGORA_APP_ID;
  const TEMP_TOKEN = token;
  const CHANNEL = resource._id;

  // check starter is course instructor or not
  const isInstructorMethod = () =>
    currentCourse?.instructors?.some(
      (instructor: any) => user?._id === instructor._id
    );
  // to leave the call

  // join the call
  useJoin(
    { appid: APP_ID!, channel: CHANNEL, token: TEMP_TOKEN, uid: uid },
    calling
  );

  usePublish([localMicrophoneTrack, localCameraTrack]);

  // play the remote user audio tracks
  audioTracks.forEach((track) => track.play);

  useEffect(() => {
    if (!currentCourse) {
      dispatch(get(id) as any);
    }
  }, [dispatch, id, currentCourse]);

  useEffect(() => {
    if (currentCourse) {
      setIsInstructor(isInstructorMethod());
    }
  }, [currentCourse, isInstructorMethod]);

  const resourceId = useCallback(() => resource?._id, [resource]);
  const userId = useCallback(() => user?._id, [user]);
  const userRole = useCallback(() => user?.role, [user]);

  const isUserRole = (
    role: string
  ): role is "teacher" | "admin" | "student" => {
    return ["teacher", "admin", "student"].includes(role);
  };

  const userByUid = (uid: number): string | undefined => {
    // remoteUsers'daki uid ile classes'daki uid eşleşmesini kontrol et
    const cls: any = classes?.find((cls: any) => cls.uid === uid);

    if (cls && cls.user) {
      return `${cls.user.name} ${cls.user.surname}`;
    }

    return undefined;
  };

  useEffect(() => {
    if (resource && uid && user && isUserRole(user?.role!)) {
      try {
        dispatch(
          generateLiveClassToken(
            resource?._id,
            uid,
            user?.role,
            user?._id!
          ) as any
        );
      } catch (error) {
        console.error(error);
      }
    }
  }, [dispatch, resourceId, userId, userRole, uid, user, resource]);

  useEffect(() => {
    if (accessToken) {
      setToken(accessToken);
    }
  }, [accessToken]);

  useEffect(() => {
    if (user) {
      setUid(Math.abs(CRC32.str(user._id!)));
    }
  }, [user]);

  useEffect(() => {
    dispatch(getActiveClasses(resource._id) as any);
  }, [dispatch, resource._id]);

  useEffect(() => {
    setClasses(activeClasses.data);
  }, [activeClasses]);

  return (
    <>
      <div className="room flex flex-col min-h-[70vh] bg-gray-100 text-white overflow-y-auto overflow-x-auto">
        {isConnected ? (
          <div className="user-list grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-1 w-full">
            <div
              id="local-stream"
              className="user relative col-span-2 w-full h-24 md:w-full md:h-48 row-span-1 flex flex-col items-center rounded-md shadow-md"
            >
              <LocalUser
                audioTrack={localMicrophoneTrack}
                cameraOn={cameraOn}
                micOn={micOn}
                videoTrack={localCameraTrack}
                className="w-full h-24 md:w-full md:h-48"
                cover="https://www.agora.io/en/wp-content/uploads/2022/10/3d-spatial-audio-icon.svg"
              >
                <span className="user-name absolute bottom-2 left-2 bg-black bg-opacity-50 text-xs px-2 py-1 rounded">
                  Ben
                </span>
              </LocalUser>
            </div>

            {remoteUsers.map((user) => (
              <div
                id="remote-streams"
                className="user w-full h-24 md:w-full md:h-48 relative flex flex-col items-center rounded-md shadow-md"
                key={user.uid}
              >
                <RemoteUser
                  cover="https://www.agora.io/en/wp-content/uploads/2022/10/3d-spatial-audio-icon.svg"
                  user={user}
                  className="w-full"
                >
                  <span className="user-name absolute bottom-2 left-2 bg-black bg-opacity-50 text-xs px-2 py-1 rounded">
                    {userByUid(+user.uid)}
                  </span>
                </RemoteUser>
              </div>
            ))}
          </div>
        ) : (
          <div className="join-room max-w-md mx-auto bg-gray-800 rounded-lg shadow-lg p-6 text-center justify-center">
            <button
              className={`join-channel ${
                !APP_ID || !CHANNEL
                  ? "bg-gray-500 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              } text-white font-bold py-2 px-4 rounded`}
              disabled={!APP_ID || !CHANNEL}
              onClick={() => setCalling(true)}
            >
              <span>Atölyeye Katıl</span>
            </button>
          </div>
        )}
      </div>
      {isConnected && (
        <div className="control justify-between items-center bg-gray-800 text-gray-200 px-6 py-3 bottom-0 w-full rounded-br rounded-bl">
          <div className="left-control flex gap-4">
            <button
              className="btn flex items-center justify-center p-2 rounded-full bg-gray-700 hover:bg-green-500 transition"
              onClick={() => setMic((a) => !a)}
            >
              <i className={`i-microphone ${!micOn ? "off opacity-50" : ""}`} />
            </button>
            <button
              className="btn flex items-center justify-center p-2 rounded-full bg-gray-700 hover:bg-blue-500 transition"
              onClick={() => setCamera((a) => !a)}
            >
              <i className={`i-camera ${!cameraOn ? "off opacity-50" : ""}`} />
            </button>
            <button
              className={`btn btn-phone p-2 rounded-full ${
                calling ? "bg-red-600" : "bg-green-500"
              } hover:opacity-90 transition`}
              onClick={() => setCalling((a) => !a)}
            >
              {calling ? (
                <FontAwesomeIcon icon={faPhoneSlash} />
              ) : (
                <FontAwesomeIcon icon={faPhone} />
              )}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
